import React, { useRef } from "react";
import { motion, useInView } from "framer-motion";
import { Helmet } from 'react-helmet-async';
import MarketingLayout from "./MarketingLayout";
import { generateBreadcrumbJsonLd } from "./utils/breadcrumbJsonLd";
import {
  CheckCircle2,
  Sparkles,
  ArrowRight,
  Package,
  TrendingUp,
  Users,
  Settings,
  Truck,
  Lock,
  Zap,
  CreditCard,
  Gift,
  Bell,
  Palette,
  Shield,
  Smartphone
} from "lucide-react";
import { Link } from "react-router-dom";

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: "easeOut" }
  }
};

function useScrollAnimation() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  return { ref, isInView };
}

const FeaturesPage: React.FC = () => {
  const heroAnimation = useScrollAnimation();
  const availableAnimation = useScrollAnimation();
  const comingSoonAnimation = useScrollAnimation();
  const additionalAnimation = useScrollAnimation();
  const ctaAnimation = useScrollAnimation();

  const availableFeatures = [
    {
      category: "基本機能",
      color: "bg-yellow-400",
      items: [
        { name: "初期費用0円・月額費用0円", desc: "サイト開設時の基本料金は0円" },
        { name: "直感的な管理画面", desc: "初心者でも簡単に操作可能なUI" },
        { name: "独自ドメイン", desc: "お好みのドメインでオリパサイトを運営" },
      ]
    },
    {
      category: "ガチャ機能",
      color: "bg-pink-400",
      items: [
        { name: "10連ガチャ・100連ガチャ", desc: "複数回数の一括抽選に対応" },
        { name: "ラストワン賞", desc: "最後の1枚に特別賞を設定" },
        { name: "キリ番賞", desc: "100回目、500回目など特定回数に特別賞" },
        { name: "1日1回限定ガチャ", desc: "ユーザーごとの回数制限で限定感を演出" },
      ]
    },
    {
      category: "決済・ポイント",
      color: "bg-green-400",
      items: [
        { name: "クレジットカード決済", desc: "主要なカードブランドに対応" },
        { name: "Apple Pay / Google Pay", desc: "サイトごとにウォレット決済を有効化" },
        { name: "ポイント機能", desc: "チャージ・利用・履歴管理" },
        { name: "ポイント交換機能", desc: "当選カード・BOXをポイントに交換" },
        { name: "クーポン機能", desc: "割引クーポンの発行・新規登録者限定オプション" },
      ]
    },
    {
      category: "セキュリティ",
      color: "bg-blue-400",
      items: [
        { name: "年齢確認（18+）", desc: "未成年の利用を制限する年齢確認機能" },
        { name: "SMS認証", desc: "電話番号の保有確認" },
        { name: "メール認証", desc: "メールアドレスの本人確認" },
        { name: "SSL/TLS暗号化", desc: "通信内容を暗号化" },
      ]
    },
    {
      category: "運営管理",
      color: "bg-purple-400",
      items: [
        { name: "在庫管理システム", desc: "商品・抽選プールの自動管理" },
        { name: "配送管理", desc: "当選商品の配送状況を追跡" },
        { name: "顧客管理", desc: "ユーザー情報・購入履歴の管理" },
        { name: "売上分析", desc: "ダッシュボードで売上を可視化" },
        { name: "お知らせ機能", desc: "ユーザーへの通知・お知らせ配信" },
        { name: "バナー管理", desc: "結果ページや配送メールにバナーを設置" },
        { name: "デザイン設定", desc: "カラー・ロゴ・デザインスタイルを自由にカスタマイズ" },
        { name: "ポケカ検索機能", desc: "TCGdex連携で簡単に商品登録" },
      ]
    },
    {
      category: "ユーザー機能",
      color: "bg-orange-400",
      items: [
        { name: "マイコレクション", desc: "当選履歴・獲得アイテムの管理" },
        { name: "Xシェア＆シェア報酬", desc: "当選結果をXでシェアしてポイント獲得" },
        { name: "通知機能", desc: "イベントや配送状況の通知" },
      ]
    },
    {
      category: "マーケティング",
      color: "bg-indigo-400",
      items: [
        { name: "友だち招待", desc: "招待コードで友だちを招待してポイントを獲得" },
      ]
    },
  ];

  const comingSoonFeatures = [
    { name: "PayPay", desc: "決済手数料9.0%で提供予定", category: "決済" },
    { name: "複数サイト運営", desc: "1アカウントで複数のオリパサイトを管理", category: "基本機能" },
    { name: "スタッフ管理", desc: "複数スタッフを招待して権限管理・共同運営", category: "基本機能" },
    { name: "アプリ機能", desc: "iOS/Androidアプリで快適なオリパ体験", category: "基本機能" },
    { name: "ポイントでカード購入", desc: "ポイントでカード・BOXを購入可能", category: "決済" },
    { name: "会員ランク機能", desc: "ポイント購入数量に応じた特典を提供", category: "マーケティング" },
    { name: "配送伝票発行", desc: "大量配送店舗向けの伝票一括発行", category: "運営効率化" },
    { name: "サムネイル自動生成", desc: "AIを活用して登録業務を効率化", category: "効率化" },
  ];

  const additionalFeatures = [
    { icon: Package, title: "在庫管理システム", description: "商品の在庫を自動管理。抽選プールも自動生成されます。", color: "bg-yellow-400" },
    { icon: TrendingUp, title: "売上分析", description: "ダッシュボードで売上や人気商品を一目で確認できます。", color: "bg-green-400" },
    { icon: Users, title: "顧客管理", description: "ユーザー情報や購入履歴を管理できます。", color: "bg-blue-400" },
    { icon: Palette, title: "デザイン設定", description: "プライマリカラー、背景色、ロゴ、デザインスタイルを自由にカスタマイズ。", color: "bg-pink-400" },
    { icon: Truck, title: "配送管理", description: "当選商品の配送状況を追跡・管理できます。", color: "bg-purple-400" },
    { icon: Shield, title: "セキュリティ", description: "SSL対応で安全な通信を実現。個人情報も厳重に管理します。", color: "bg-orange-400" },
  ];

  return (
    <MarketingLayout>
      <Helmet>
        <title>オリパサイト構築に必要な機能一覧 | JOYBASE</title>
        <meta name="description" content="JOYBASEでオリパサイトを構築する際に使える全機能をご紹介。初期費用0円で10連ガチャ、ラストワン賞、ポイント機能、在庫管理、顧客管理など、オリパ構築・運営に必要な機能がすべて揃っています。" />
        <meta name="keywords" content="オリパ 構築 機能, オリパサイト 機能, オリパ 10連ガチャ, オリパ ラストワン賞, オリパ ポイント機能, オリパ 在庫管理, オリパ 顧客管理, オリパサイト 作り方" />
        <meta name="robots" content="index, follow" />
        <meta property="og:title" content="オリパサイト構築に必要な機能一覧 | JOYBASE" />
        <meta property="og:description" content="JOYBASEでオリパサイトを構築する際に使える全機能をご紹介。初期費用0円で本格的なオリパサイトを構築できます。" />
        <meta property="og:url" content="https://www.joybase.jp/features" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="JOYBASE" />
        <meta property="og:image" content="https://www.joybase.jp/ogp.svg" />
        <meta property="og:locale" content="ja_JP" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="オリパサイト構築に必要な機能一覧 | JOYBASE" />
        <meta name="twitter:description" content="JOYBASEの全機能をご紹介。10連ガチャ、ラストワン賞、ポイント機能など充実の機能。" />
        <meta name="twitter:image" content="https://www.joybase.jp/ogp.svg" />
        <link rel="canonical" href="https://www.joybase.jp/features" />
        <script type="application/ld+json">
        {generateBreadcrumbJsonLd([
          { name: "ホーム", url: "https://www.joybase.jp/" },
          { name: "特徴", url: "https://www.joybase.jp/features" }
        ])}
        </script>
        <script type="application/ld+json">
        {`
          {
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": "オリパサイト構築に必要な機能一覧",
            "description": "JOYBASEでオリパサイトを構築する際に使える全機能をご紹介。初期費用0円で本格的なオリパサイトを構築できます。",
            "url": "https://www.joybase.jp/features",
            "mainEntity": {
              "@type": "ItemList",
              "name": "JOYBASE機能一覧",
              "numberOfItems": 30,
              "itemListElement": [
                {
                  "@type": "ListItem",
                  "position": 1,
                  "name": "10連ガチャ・100連ガチャ",
                  "description": "複数回数の一括抽選で売上アップをサポート"
                },
                {
                  "@type": "ListItem",
                  "position": 2,
                  "name": "ラストワン賞",
                  "description": "最後の1枚に特別賞を設定して購買意欲を刺激"
                },
                {
                  "@type": "ListItem",
                  "position": 3,
                  "name": "ポイント機能",
                  "description": "ポイントのチャージ・利用・履歴管理が可能"
                },
                {
                  "@type": "ListItem",
                  "position": 4,
                  "name": "在庫管理システム",
                  "description": "商品・抽選プールを自動で管理"
                },
                {
                  "@type": "ListItem",
                  "position": 5,
                  "name": "顧客管理",
                  "description": "ユーザー情報・購入履歴を一元管理"
                }
              ]
            }
          }
        `}
        </script>
        <script type="application/ld+json">
        {`
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "JOYBASEで使えるガチャ機能は何がありますか？",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "JOYBASEでは10連ガチャ・100連ガチャ、ラストワン賞、キリ番賞などの機能が利用できます。これらの機能で売上アップと購買意欲の向上をサポートします。"
                }
              },
              {
                "@type": "Question",
                "name": "オリパサイトの運営管理機能にはどのようなものがありますか？",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "在庫管理システム、配送管理、顧客管理、売上分析ダッシュボード、お知らせ機能、サイトカスタマイズ機能など、オリパサイト運営に必要な機能がすべて揃っています。"
                }
              },
              {
                "@type": "Question",
                "name": "決済機能やセキュリティはどうなっていますか？",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "クレジットカード決済（Stripe）、ポイント機能を提供しています。セキュリティ面ではSMS認証による本人確認、SSL/TLS暗号化通信で安全性を確保しています。"
                }
              }
            ]
          }
        `}
        </script>
      </Helmet>

      <div className="min-h-screen bg-[#FFFEF5]">
        {/* Hero Section */}
        <section className="pt-24 pb-16 px-4 sm:px-6 lg:px-8">
          <motion.div
            ref={heroAnimation.ref}
            initial="hidden"
            animate={heroAnimation.isInView ? "visible" : "hidden"}
            variants={containerVariants}
            className="max-w-5xl mx-auto text-center"
          >
            <motion.div
              variants={itemVariants}
              className="inline-flex items-center gap-2 px-4 py-2 bg-yellow-400 border-4 border-black font-black mb-6"
            >
              <Sparkles className="w-5 h-5" />
              FEATURES
            </motion.div>

            <motion.h1
              variants={itemVariants}
              className="text-4xl sm:text-5xl font-black mb-6"
            >
              オリパサイト構築に必要な機能
            </motion.h1>

            <motion.p
              variants={itemVariants}
              className="text-lg text-gray-700 max-w-3xl mx-auto mb-8"
            >
              初期費用0円・月額費用0円で、本格的なオリパサイトを構築・運営できる充実の機能をご提供します。
            </motion.p>

            {/* Quick Stats */}
            <motion.div
              variants={itemVariants}
              className="flex flex-wrap justify-center gap-4"
            >
              {[
                { icon: Zap, label: "25+機能", color: "bg-yellow-400" },
                { icon: CreditCard, label: "決済対応", color: "bg-green-400" },
                { icon: Gift, label: "ガチャ機能", color: "bg-pink-400" },
                { icon: Lock, label: "セキュリティ", color: "bg-blue-400" },
              ].map((item, index) => (
                <div
                  key={index}
                  className={`${item.color} border-4 border-black px-4 py-2 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] flex items-center gap-2`}
                >
                  <item.icon className="w-5 h-5" />
                  <span className="font-black">{item.label}</span>
                </div>
              ))}
            </motion.div>
          </motion.div>
        </section>

        {/* Available Features */}
        <section className="py-16 px-4 sm:px-6 lg:px-8">
          <motion.div
            ref={availableAnimation.ref}
            initial="hidden"
            animate={availableAnimation.isInView ? "visible" : "hidden"}
            variants={containerVariants}
            className="max-w-6xl mx-auto"
          >
            <motion.div variants={itemVariants} className="text-center mb-12">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-400 border-4 border-black font-black mb-4">
                <CheckCircle2 className="w-5 h-5" />
                提供中
              </div>
              <h2 className="text-3xl font-black mb-4">現在ご利用いただける機能</h2>
              <p className="text-gray-600">今すぐ使える充実の機能でオリパサイト運営をスタート</p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {availableFeatures.map((category, categoryIndex) => (
                <motion.div
                  key={categoryIndex}
                  variants={itemVariants}
                  className="bg-white border-4 border-black shadow-[6px_6px_0px_0px_rgba(0,0,0,1)]"
                >
                  <div className={`${category.color} border-b-4 border-black px-4 py-3`}>
                    <h3 className="font-black">{category.category}</h3>
                  </div>
                  <div className="p-4 space-y-3">
                    {category.items.map((item, itemIndex) => (
                      <div key={itemIndex} className="flex items-start gap-2">
                        <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="font-bold text-sm">{item.name}</p>
                          <p className="text-xs text-gray-500">{item.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </section>

        {/* Coming Soon Features */}
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-black text-white">
          <motion.div
            ref={comingSoonAnimation.ref}
            initial="hidden"
            animate={comingSoonAnimation.isInView ? "visible" : "hidden"}
            variants={containerVariants}
            className="max-w-6xl mx-auto"
          >
            <motion.div variants={itemVariants} className="text-center mb-12">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-400 text-black border-4 border-white font-black mb-4">
                <Sparkles className="w-5 h-5" />
                開発中
              </div>
              <h2 className="text-3xl font-black mb-4">今後提供予定の機能</h2>
              <p className="text-gray-400">さらに便利になる新機能を開発中です</p>
            </motion.div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {comingSoonFeatures.map((feature, index) => (
                <motion.div
                  key={index}
                  variants={itemVariants}
                  className="bg-gray-900 border-2 border-gray-700 p-4 hover:border-yellow-400 transition-colors"
                >
                  <span className="inline-block px-2 py-1 bg-gray-800 text-xs font-bold text-gray-400 mb-2">
                    {feature.category}
                  </span>
                  <h3 className="font-black mb-1">{feature.name}</h3>
                  <p className="text-sm text-gray-500">{feature.desc}</p>
                </motion.div>
              ))}
            </div>

            <motion.p
              variants={itemVariants}
              className="text-center text-gray-500 text-sm mt-8"
            >
              ※ 開発予定の機能は変更になる場合があります
            </motion.p>
          </motion.div>
        </section>

        {/* Additional Features Grid */}
        <section className="py-16 px-4 sm:px-6 lg:px-8">
          <motion.div
            ref={additionalAnimation.ref}
            initial="hidden"
            animate={additionalAnimation.isInView ? "visible" : "hidden"}
            variants={containerVariants}
            className="max-w-6xl mx-auto"
          >
            <motion.div variants={itemVariants} className="text-center mb-12">
              <h2 className="text-3xl font-black mb-4">その他の充実機能</h2>
              <p className="text-gray-600">運営をサポートする便利な機能も多数搭載</p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {additionalFeatures.map((feature, index) => (
                <motion.div
                  key={index}
                  variants={itemVariants}
                  whileHover={{ y: -4 }}
                  className="bg-white border-4 border-black shadow-[6px_6px_0px_0px_rgba(0,0,0,1)] hover:shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] transition-all p-6"
                >
                  <div className={`w-14 h-14 ${feature.color} border-4 border-black flex items-center justify-center mb-4`}>
                    <feature.icon className="w-7 h-7" />
                  </div>
                  <h3 className="text-xl font-black mb-2">{feature.title}</h3>
                  <p className="text-gray-600">{feature.description}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </section>

        {/* CTA Section */}
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-yellow-400 border-t-4 border-black">
          <motion.div
            ref={ctaAnimation.ref}
            initial="hidden"
            animate={ctaAnimation.isInView ? "visible" : "hidden"}
            variants={containerVariants}
            className="max-w-4xl mx-auto text-center"
          >
            <motion.h2
              variants={itemVariants}
              className="text-3xl sm:text-4xl font-black mb-6"
            >
              主要機能を初期費用0円で利用できます
            </motion.h2>

            <motion.p
              variants={itemVariants}
              className="text-lg text-gray-800 mb-8"
            >
              無料でアカウントを作成し、管理画面から利用できる機能をご確認ください
            </motion.p>

            <motion.div
              variants={itemVariants}
              className="flex flex-col sm:flex-row gap-4 justify-center"
            >
              <a
                href={window.location.hostname.includes('localhost')
                  ? `http://dashboard.joybase.localhost:${window.location.port}/signup`
                  : 'https://dashboard.joybase.jp/signup'}
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-black text-white font-black text-lg border-4 border-black shadow-[4px_4px_0px_0px_rgba(255,255,255,1)] hover:shadow-[2px_2px_0px_0px_rgba(255,255,255,1)] hover:translate-x-[2px] hover:translate-y-[2px] transition-all"
              >
                無料でサイト開設を申し込む
                <ArrowRight className="w-5 h-5" />
              </a>
              <Link
                to="/for_shops/how-to-start"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-black font-black text-lg border-4 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] hover:shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] hover:translate-x-[2px] hover:translate-y-[2px] transition-all"
              >
                始め方ガイドを見る
              </Link>
            </motion.div>
          </motion.div>
        </section>
      </div>
    </MarketingLayout>
  );
};

export default FeaturesPage;
